import { MeasurementsQueryResult } from '../../../timeSeries/types'
import HighchartsChart from '@/components/Charts/HighchartsChart'
import Highcharts from 'highcharts'
import { parseFirstMeasurementAggOrValue } from '../utils/parseFirstMeasurementAggOrValue'
import { CommonChartProps, useCommonChartOptions } from '../commonChartOptions'

export interface TimeSeriesAreaChartOptions {
    stack?: boolean
    yAxisBounds?: { min?: number; max?: number }
}

interface TimeSeriesAreaChartProps extends CommonChartProps, TimeSeriesAreaChartOptions {
    series: MeasurementsQueryResult[]
    options?: TimeSeriesAreaChartOptions & CommonChartProps
}

const getChartFormatOptions = (options?: TimeSeriesAreaChartOptions & CommonChartProps): Highcharts.Options => ({
    title: {
        text: '',
    },
    chart: {
        backgroundColor: 'transparent',
        type: 'area',
    },
    yAxis: {
        title: {
            text: '',
        },
        ...options?.yAxisBounds,
    },
    plotOptions: {
        series: {
            animation: false,
        },
        area: {
            marker: { enabled: false },
            stacking: options?.stack ? 'normal' : undefined,
            fillOpacity: 0.5,
        },
    },
})

const isTimeAggregated = (series: MeasurementsQueryResult[]): boolean => {
    try {
        const firstMeasurementTimestamp = series[0].data.measurements[0].timestamp
        return new Date(firstMeasurementTimestamp).getFullYear() === 1970
    } catch (e) {
        return false
    }
}

const getDateTimeLabelFormats = (series: MeasurementsQueryResult[]): string | undefined => {
    return isTimeAggregated(series) ? '<b>%H:%M</b>' : undefined
}

const timeProfileXAxisOptions = {
    type: 'datetime',
    labels: {
        format: '{value:%H:%M}',
    },
    min: new Date(1970, 0, 1).setHours(0, 0, 0, 0),
    max: new Date(1970, 0, 2).setHours(0, 0, 0, 0), // Set to 24:00
    startOnTick: true,
    endOnTick: true,
}

const AreaChart: React.FC<TimeSeriesAreaChartProps> = ({ series, options, disableLegend, ...highchartChartProps }) => {
    const isTimeProfile = isTimeAggregated(series)
    const commonOptions = useCommonChartOptions()
    return (
        <HighchartsChart
            type="area"
            options={{
                ...commonOptions,
                ...getChartFormatOptions(options),
                tooltip: {
                    pointFormat: `{series.name}: <b>{point.y:.0f} ${series[0].data.unit || ''}</b>`,
                    xDateFormat: getDateTimeLabelFormats(series),
                    shared: true,
                },
                series: series.map((s) => ({
                    name: s.data.label || s.data.meterName,
                    data: s.data.measurements.map((m) => ({
                        x: m.timestamp,
                        y: parseFirstMeasurementAggOrValue(m),
                    })),
                    color: s.data.color,
                })),
                legend: {
                    enabled: !disableLegend,
                },
                xAxis: isTimeProfile ? timeProfileXAxisOptions : commonOptions.xAxis,
                time: {
                    timezone: series[0].data.timeZone,
                },
            }}
            {...highchartChartProps}
        />
    )
}

export default AreaChart
